import React, { useContext, useEffect, useState } from 'react';
import {
  ExportV2LimitedLineup,
  ExportV2LimitedResult,
} from '../../../../../../../shared/types/exportV2';
import axios from 'axios';
import { mapBy, MappedItems } from '../../../../../../../shared/lib/functional';
import { DailyProgramCollectionMaps } from '../../../../../../../shared/types/dailyProgram';
import { useQueryStrings } from './QueryStringContext';
import { NaveganteDataResponse } from '../../..';
import { DEFAULT_APP_CONFIG, NaveganteAppConfig } from '../../../../../../../shared/lib/navegante';
import LoadingPage from '../views/LoadingPage';
import NoDataPage from '../views/NoDataPage';
import { useApp } from './AppContext';

const DataContext = React.createContext<DataProviderPropsBase | undefined>(undefined);

export function useData() {
  const context = useContext(DataContext);

  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }

  return context;
}

export interface ExportOffer {
  id: string;
  name: string;
  description: string;
  venueId: string;
  trending: boolean;
  status: string;
  typeIds: string[];
  callToActions: ExportCta[];
  images: ExportImage[];
}

export interface ExportMessage {
  id: string;
  title: string;
  description: string;
}

export interface ExportInformation {
  id: string;
  title: string;
  description: string;
  callToActions: ExportCta[];
  isQuestionAnswer: boolean;
}

export interface ExportCta {
  label: string;
  link: string;
  target: string;
}

export interface ExportImage {
  url: string;
  types: string[];
}

export interface OfferTypeRef {
  id: string;
  name: string;
}

interface WithDataPropsBase {}

type WithDataProps = WithDataPropsBase & React.HTMLAttributes<HTMLDivElement>;

type DataResponse = Omit<ExportV2LimitedResult, 'lineup' | 'collections'> & {
  lineup: MappedItems<ExportV2LimitedLineup>;
  collections: Omit<DailyProgramCollectionMaps, 'dayThemes'>;
  appConfig: NaveganteAppConfig;
};

export function WithData(props: WithDataProps) {
  const { qs } = useQueryStrings();
  const [data, setData] = useState<DataResponse | undefined>(undefined);
  const [tick, setTick] = useState(0);
  const [loading, setLoading] = useState(false);
  const { config } = useApp();

  useEffect(() => {
    loadData().catch(console.error);
  }, [qs.date, qs.lang, tick]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(tick => (tick + 1) % 10);
    }, 60 * 1000); // every minute, set tick

    return () => {
      clearInterval(interval);
    };
  }, []);

  // WARNING: If you would like to use state to further complex this refresh function,
  // see UserInfoContext.tsx for an example of how you can do this.
  async function loadData() {
    if (loading) return;

    setLoading(true);

    const result: DataResponse = {
      code: 10,
      collections: {
        segments: {},
        levels: {},
        attributes: {},
        contentSegments: {},
        contentLevels: {},
        contentAttributes: {},
        highlights: {},
        venueTypes: {},
        offerTypes: {},
        zones: {},
        eventTemplates: {},
        venueTemplates: {},
        contentTemplates: {},
        levelLabel: '',
      },
      lineup: {},
      appConfig: {
        ...DEFAULT_APP_CONFIG,
      },
    };

    try {
      const response = await axios.get<NaveganteDataResponse>(config.dataUrl, {
        params: {
          lang: qs.lang,
        },
      });

      const { exportData, appConfig } = response.data;

      result.collections.eventTemplates = mapBy(exportData.collections.eventTemplates, '_id');
      result.collections.venueTemplates = mapBy(exportData.collections.venueTemplates, '_id');
      result.collections.segments = mapBy(exportData.collections.segments, '_id');
      result.collections.levels = mapBy(exportData.collections.levels, '_id');
      result.collections.attributes = mapBy(exportData.collections.attributes, '_id');
      result.collections.venueTypes = mapBy(exportData.collections.venueTypes, '_id');
      result.collections.offerTypes = mapBy(exportData.collections.offerTypes, '_id');
      result.collections.zones = mapBy(exportData.collections.zones, '_id');
      result.collections.levelLabel = exportData.collections.levelLabel;

      result.lineup = mapBy(exportData.lineup, 'date');
      result.appConfig = appConfig;
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }

    setData(result);
  }

  if (!data) return <LoadingPage />;

  const todayLineup = data.lineup[config.date];
  const dateLineup = data.lineup[qs.date];

  if (!todayLineup || !dateLineup) return <NoDataPage />;

  return (
    <DataProvider data={data} todayLineup={todayLineup} dateLineup={dateLineup}>
      {props.children}
    </DataProvider>
  );
}

interface DataProviderPropsBase {
  data: DataResponse;
  todayLineup: ExportV2LimitedLineup;
  dateLineup: ExportV2LimitedLineup;
}

type DataProviderProps = DataProviderPropsBase & {
  children?: React.ReactNode;
};

export default function DataProvider(props: DataProviderProps) {
  const { children, ...rest } = props;

  return <DataContext.Provider value={rest}>{children}</DataContext.Provider>;
}
